import React, { useEffect, useMemo, useState } from 'react';
import { DepreciationResult, FixedAsset } from './fixedAssetTypes';
import { getErrorMessage } from '../utils/errorHandler';

interface DepreciationCalculatorProps {
  asset: FixedAsset | null;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

type ViewMode = 'monthly' | 'annual';

const DepreciationCalculator: React.FC<DepreciationCalculatorProps> = ({ asset, apiFetch }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('monthly');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [monthlyResults, setMonthlyResults] = useState<DepreciationResult[]>([]);
  const [annualResults, setAnnualResults] = useState<DepreciationResult[]>([]);
  const [lastCalculatedAt, setLastCalculatedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!asset) {
      setMonthlyResults([]);
      setAnnualResults([]);
      setStartDate('');
      setEndDate('');
      setInfo(null);
      setError(null);
      return;
    }
    setStartDate(asset.service_start_date ?? '');
    setEndDate('');
    setInfo(null);
    setError(null);
    setViewMode('monthly');
  }, [asset]);

  const handleCalculate = async () => {
    if (!asset) {
      return;
    }

    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const payload: Record<string, any> = {
        calculation_type: viewMode,
      };
      if (startDate) {
        payload.start_date = startDate;
      }
      if (endDate) {
        payload.end_date = endDate;
      }

      const response = await apiFetch(`/api/fixed-assets/${asset.id}/calculate-depreciation`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '減価償却計算に失敗しました。');
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setMonthlyResults(data.monthly_results ?? []);
      setAnnualResults(data.annual_results ?? []);
      setLastCalculatedAt(new Date().toISOString());
      setInfo('減価償却計算が完了しました。');
    } catch (err: any) {
      const message = err?.message || '減価償却計算中にエラーが発生しました。';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const activeResults = useMemo(() => {
    return viewMode === 'monthly' ? monthlyResults : annualResults;
  }, [viewMode, monthlyResults, annualResults]);

  const formatDate = (value: string) => {
    if (!value) {
      return '';
    }
    try {
      return new Date(value).toISOString().slice(0, 10);
    } catch {
      return value;
    }
  };

  const formatCurrency = (value: number) => value.toLocaleString('ja-JP');

  const totalDepreciation = useMemo(() => {
    if (activeResults.length === 0) {
      return 0;
    }
    return activeResults[activeResults.length - 1].accumulated_depreciation;
  }, [activeResults]);

  return (
    <div
      style={{
        border: '2px inset #d0d0d0',
        padding: '12px',
        backgroundColor: '#ffffff',
        minWidth: '360px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          color: '#003366',
        }}
      >
        減価償却計算
      </div>

      {!asset ? (
        <div style={{ fontSize: '11px', color: '#666' }}>
          固定資産を選択すると、減価償却計算を実行できます。
        </div>
      ) : (
        <>
          <div
            style={{
              fontSize: '11px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #d0d0d0',
              padding: '8px',
              lineHeight: 1.6,
            }}
          >
            <div>資産コード: <strong>{asset.asset_code}</strong></div>
            <div>資産名称: {asset.asset_name}</div>
            <div>取得価格: {formatCurrency(asset.acquisition_cost)}円 / 耐用年数: {asset.useful_life_years}年</div>
            <div>供用開始日: {formatDate(asset.service_start_date)}</div>
          </div>

          {error && (
            <div
              style={{
                backgroundColor: '#ffebee',
                border: '1px solid #c62828',
                color: '#c62828',
                padding: '6px',
                fontSize: '11px',
              }}
            >
              {error}
            </div>
          )}

          {info && (
            <div
              style={{
                backgroundColor: '#e8f5e9',
                border: '1px solid #2e7d32',
                color: '#2e7d32',
                padding: '6px',
                fontSize: '11px',
              }}
            >
              {info}
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '8px',
              fontSize: '11px',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column' }}>
              計算期間の開始日
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ padding: '4px', border: '1px inset #d0d0d0' }}
                disabled={loading}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column' }}>
              計算期間の終了日
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ padding: '4px', border: '1px inset #d0d0d0' }}
                disabled={loading}
              />
            </label>
          </div>

          <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="radio"
                name="calculationType"
                value="monthly"
                checked={viewMode === 'monthly'}
                onChange={() => setViewMode('monthly')}
                disabled={loading}
              />
              月次
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="radio"
                name="calculationType"
                value="annual"
                checked={viewMode === 'annual'}
                onChange={() => setViewMode('annual')}
                disabled={loading}
              />
              年次
            </label>
            <button
              type="button"
              onClick={handleCalculate}
              disabled={loading}
              style={{
                marginLeft: 'auto',
                backgroundColor: '#0066cc',
                color: '#ffffff',
                border: '2px outset #004c99',
                padding: '4px 12px',
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              {loading ? '計算中...' : '計算を実行'}
            </button>
          </div>

          {lastCalculatedAt && (
            <div style={{ fontSize: '10px', color: '#666' }}>
              最終計算日時: {new Date(lastCalculatedAt).toLocaleString('ja-JP')}
            </div>
          )}

          <div
            style={{
              fontSize: '11px',
              fontWeight: 'bold',
              marginTop: '4px',
            }}
          >
            計算結果 ({viewMode === 'monthly' ? '月次' : '年次'}) / 累計償却額: {formatCurrency(totalDepreciation)}円
          </div>

          <div style={{ flex: 1, overflow: 'auto', border: '1px inset #d0d0d0', maxHeight: '220px' }}>
            {activeResults.length === 0 ? (
              <div style={{ padding: '12px', fontSize: '11px', color: '#666' }}>
                まだ計算結果はありません。条件を指定して「計算を実行」を押してください。
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e0e0e0' }}>
                    <th style={{ border: '1px solid #999', padding: '4px', width: '110px' }}>
                      期間開始
                    </th>
                    <th style={{ border: '1px solid #999', padding: '4px', width: '110px' }}>
                      期間終了
                    </th>
                    <th style={{ border: '1px solid #999', padding: '4px', width: '100px' }}>
                      減価償却額
                    </th>
                    <th style={{ border: '1px solid #999', padding: '4px', width: '110px' }}>
                      累計償却額
                    </th>
                    <th style={{ border: '1px solid #999', padding: '4px', width: '110px' }}>
                      帳簿価額
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activeResults.map((row, index) => (
                    <tr key={`${row.period_start}-${row.period_end}-${index}`}>
                      <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                        {formatDate(row.period_start)}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                        {formatDate(row.period_end)}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                        {formatCurrency(row.depreciation_amount)}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                        {formatCurrency(row.accumulated_depreciation)}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                        {formatCurrency(row.book_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DepreciationCalculator;
